import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchItem, type HNItem } from "@/lib/hn";
import { CommentItem } from "@/components/comment/CommentItem";
import { Loader2, ArrowLeft, Bookmark } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { getSavedPosts, savePost, unsavePost } from "@/lib/storage";

export function PostPage() {
  const { id } = useParams<{ id: string }>();
  const postId = Number(id);
  const queryClient = useQueryClient();

  const { data: post, isLoading, error } = useQuery<HNItem>({
    queryKey: ["item", postId],
    queryFn: () => fetchItem(postId),
    enabled: !isNaN(postId),
  });

  // Saved state management
  const { data: savedPosts } = useQuery<HNItem[]>({
    queryKey: ["savedPosts"],
    queryFn: async () => {
      return getSavedPosts();
    },
    staleTime: Infinity,
  });

  const isEffectivelySaved = !!savedPosts?.find((sp: HNItem) => sp.id === postId);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!post) return;
      if (isEffectivelySaved) {
        unsavePost(post.id);
      } else {
        savePost(post);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedPosts"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-on-surface-variant">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-secondary" />
        <p className="font-label font-bold tracking-widest uppercase text-xs">Loading thread...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="p-4 bg-error/20 text-error rounded-none border border-error">
        Failed to load thread.
      </div>
    );
  }

  let domain = "";
  try {
    domain = post.url ? new URL(post.url).hostname.replace("www.", "") : "";
  } catch {
    console.error("Invalid URL:", post.url);
  }

  return (
    <div className="animate-in fade-in duration-500 w-full max-w-5xl mx-auto">
      {/* Back navigation */}
      <div className="mb-6">
        <Link
          to="/hn"
          className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest text-on-surface-variant hover:text-white transition-colors uppercase"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to Feed
        </Link>
      </div>

      {/* Original Post Section (Highlighted surface) */}
      <article className="relative group">
        <div className="relative bg-white rounded-2xl p-8 mb-10 shadow-2xl overflow-hidden border border-white/20">
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-center gap-1 mt-1">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-surface-variant transition-all duration-300">
                <span className="material-symbols-outlined text-primary group-hover:text-on-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>keyboard_arrow_up</span>
              </div>
              <span className="text-primary font-headline font-bold text-lg">{post.score || 0}</span>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-secondary text-on-secondary text-[10px] font-bold uppercase tracking-tighter px-2 py-0.5 rounded-md">Trending</span>
                <span className="text-on-surface-variant text-xs font-label uppercase tracking-widest">Discussion</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-headline font-bold text-[#1a1a1a] tracking-tight mb-4 leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#666666] text-xs font-label">
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">person</span>
                  by <span className="text-primary font-bold">{post.by}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">schedule</span>
                  {formatDistanceToNow(post.time * 1000, { addSuffix: true })}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">chat_bubble</span>
                  {post.descendants || 0} comments
                </span>
                {domain && (
                  <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">link</span>
                    {domain}
                  </a>
                )}
              </div>
            </div>
          </div>

          {post.text && (
            <div
              className="mt-8 pt-8 border-t border-gray-200 text-[#1a1a1a]/80 leading-relaxed text-lg font-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.text) }}
            />
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={(e) => { e.preventDefault(); saveMutation.mutate(); }}
              disabled={saveMutation.isPending}
              className={`flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-lg transition-colors ${
                isEffectivelySaved ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Bookmark className={`w-4 h-4 ${isEffectivelySaved ? 'fill-white' : ''}`} />
              {isEffectivelySaved ? 'Saved to List' : 'Bookmark Story'}
            </button>
          </div>
        </div>
      </article>

      {/* Discussion Action Bar */}
      <div className="flex items-center justify-between mb-8 px-2">
        <h2 className="font-headline font-bold text-xl text-white flex items-center gap-3">
          Discussion <span className="text-white/60 text-sm font-normal">({post.descendants || 0})</span>
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs font-label text-white transition-colors">
            <span className="material-symbols-outlined text-sm">sort</span>
            Top Comments
          </div>
          <div className="bg-white/10 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all">
            Refresh Thread
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="space-y-4">
        {post.kids && post.kids.length > 0 ? (
          post.kids.map((kidId) => (
            <CommentItem key={kidId} id={kidId} isRoot={true} />
          ))
        ) : (
          <div className="p-12 text-center text-white/50 border border-white/20 rounded-xl bg-surface-container-low/30">
            <p className="font-serif italic text-lg">No comments yet in this thread.</p>
          </div>
        )}
      </div>

      {/* Sticky Mobile Reply (Optional visual element from design) */}
      <div className="fixed bottom-6 right-6 md:hidden z-50">
        <button className="w-14 h-14 bg-secondary text-on-secondary rounded-full shadow-2xl flex items-center justify-center transition-transform active:scale-90 electric-glow">
          <span className="material-symbols-outlined">add_comment</span>
        </button>
      </div>
    </div>
  );
}
